/** Short human detail from tool args/result so rows aren't identical blanks. */

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pickString(value: unknown, keys: string[]): string | undefined {
  const obj = asRecord(value);
  if (!obj) return undefined;
  for (const key of keys) {
    const raw = obj[key];
    if (typeof raw === "string" && raw.trim()) return raw.trim();
  }
  return undefined;
}

function truncate(text: string, max = 52): string {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (!cleaned) return cleaned;
  return cleaned.length > max ? `${cleaned.slice(0, max - 1)}…` : cleaned;
}

function basenamePath(path: string): string {
  const parts = path.replace(/\\/g, "/").split("/").filter(Boolean);
  return parts.at(-1) ?? path;
}

function platformList(args: unknown): string | null {
  const obj = asRecord(args);
  const platforms = obj && Array.isArray(obj.platforms) ? obj.platforms : [];
  const names = platforms
    .map((p) => {
      if (!p || typeof p !== "object") return "";
      const row = p as Record<string, unknown>;
      return (
        (typeof row.label === "string" && row.label) ||
        (typeof row.platform === "string" && row.platform) ||
        (typeof row.id === "string" && row.id) ||
        ""
      );
    })
    .map((n) => n.trim())
    .filter(Boolean);
  if (!names.length) return null;
  if (names.length <= 3) return truncate(names.join(", "));
  return truncate(`${names.slice(0, 2).join(", ")} +${names.length - 2}`);
}

function etlBranchSummary(args: unknown): string | null {
  const obj = asRecord(args);
  if (!obj) return null;
  if (typeof obj.platform === "string" && obj.platform.trim()) {
    return truncate(obj.platform);
  }
  const branches = Array.isArray(obj.branches) ? obj.branches : [];
  const platforms = branches
    .map((b) => {
      if (!b || typeof b !== "object") return "";
      const row = b as Record<string, unknown>;
      return (
        (typeof row.platform === "string" && row.platform) ||
        (typeof row.app === "string" && row.app) ||
        ""
      );
    })
    .map((n) => n.trim())
    .filter(Boolean);
  if (!platforms.length) return branches.length ? `${branches.length} branches` : null;
  if (platforms.length <= 3) return truncate(platforms.join(", "));
  return truncate(`${platforms.slice(0, 2).join(", ")} +${platforms.length - 2}`);
}

/** Toolkit / app slugs from tool args for Composio logo chips. */
export function toolActivityPlatforms(tool: string, args?: unknown): string[] {
  const t = tool.trim();
  const seen = new Set<string>();
  const push = (raw: string | undefined) => {
    const v = raw?.trim().toLowerCase();
    if (v) seen.add(v);
  };

  switch (t) {
    case "search_tools":
    case "get_connect_link":
      push(pickString(args, ["app", "toolkit", "platform"]));
      break;
    case "update_investigation_coverage": {
      const obj = asRecord(args);
      const platforms = obj && Array.isArray(obj.platforms) ? obj.platforms : [];
      for (const p of platforms) {
        if (!p || typeof p !== "object") continue;
        const row = p as Record<string, unknown>;
        push(
          (typeof row.platform === "string" && row.platform) ||
            (typeof row.id === "string" && row.id) ||
            (typeof row.label === "string" && row.label) ||
            undefined,
        );
      }
      break;
    }
    case "start_investigation_etl": {
      push(pickString(args, ["platform", "app"]));
      const obj = asRecord(args);
      const branches = obj && Array.isArray(obj.branches) ? obj.branches : [];
      for (const b of branches) {
        if (!b || typeof b !== "object") continue;
        const row = b as Record<string, unknown>;
        push(
          (typeof row.platform === "string" && row.platform) ||
            (typeof row.app === "string" && row.app) ||
            undefined,
        );
      }
      break;
    }
    case "get_tool_schema": {
      const slug = pickString(args, ["tool_slug", "toolId", "slug", "tool"]);
      if (slug) {
        const prefix = slug.split(/[_-]/)[0];
        if (prefix && prefix.length > 2) push(prefix);
      }
      break;
    }
    default:
      push(pickString(args, ["platform", "app", "toolkit"]));
  }

  return [...seen];
}

/**
 * One-line context for a tool row (skill name, search query, slug, …).
 * Prefer args; fall back to a few result fields when useful.
 */
export function summarizeToolActivity(
  tool: string,
  args?: unknown,
  result?: unknown,
): string | null {
  const t = tool.trim();

  switch (t) {
    case "load_skill":
    case "search_skills": {
      const fromArgs = pickString(args, [
        "skill",
        "name",
        "skill_name",
        "skillId",
        "id",
        "query",
        "q",
      ]);
      if (fromArgs) return truncate(fromArgs);
      return pickString(result, ["skill", "name", "id"])
        ? truncate(pickString(result, ["skill", "name", "id"])!)
        : null;
    }
    case "skill_read": {
      const path = pickString(args, ["path", "file", "relative_path", "name", "skill"]);
      return path ? truncate(basenamePath(path)) : null;
    }
    case "search_tools": {
      const q = pickString(args, ["use_case", "query"]);
      const app = pickString(args, ["app"]);
      if (q && app) return truncate(`${app}: ${q}`);
      return q ? truncate(q) : app ? truncate(app) : null;
    }
    case "get_tool_schema": {
      const slug = pickString(args, ["tool_slug", "toolId", "slug", "tool"]);
      return slug ? truncate(slug) : null;
    }
    case "ask_user": {
      const q = pickString(args, ["question", "prompt"]);
      return q ? truncate(q, 64) : null;
    }
    case "update_investigation_coverage":
      return platformList(args);
    case "start_investigation_etl":
      return etlBranchSummary(args);
    case "get_etl_status": {
      const wait = pickString(args, ["wait_until", "waitUntil"]);
      return wait ? truncate(wait.replace(/_/g, " ")) : null;
    }
    case "list_investigation_schema": {
      const rel = pickString(args, ["relation", "table", "dataset", "name"]);
      return rel ? truncate(rel) : null;
    }
    case "analytics_query": {
      const obj = asRecord(args);
      const queries = obj && Array.isArray(obj.queries) ? obj.queries : [];
      if (queries.length === 1) {
        const id = pickString(queries[0], ["id", "name"]);
        return id ? truncate(id) : "1 query";
      }
      if (queries.length > 1) return `${queries.length} queries`;
      return null;
    }
    case "web_search": {
      const q = pickString(args, ["query"]);
      return q ? truncate(q) : null;
    }
    case "inspect_dataset": {
      const id = pickString(args, ["datasetId", "dataset_id", "id"]);
      const path = pickString(args, ["path"]);
      if (id && path) return truncate(`${id} · ${path}`);
      return id ? truncate(id) : path ? truncate(path) : null;
    }
    case "list_datasets":
      return null;
    case "get_connect_link": {
      const toolkit = pickString(args, ["toolkit", "app"]);
      return toolkit ? truncate(toolkit) : null;
    }
    case "execute_typescript": {
      const name = pickString(args, ["name", "title", "description"]);
      return name ? truncate(name) : null;
    }
    default: {
      const generic = pickString(args, [
        "query",
        "q",
        "use_case",
        "name",
        "title",
        "path",
        "skill",
        "tool_slug",
        "platform",
        "question",
      ]);
      return generic ? truncate(generic) : null;
    }
  }
}
